import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import FieldMapper from '@components/mappings/FieldMapper';
import { mappingApi } from '@services/api';
import type { SourceSystem, FieldMapping, MappingRequest } from '@types';

interface MappingFormData {
  name: string;
  source_system: SourceSystem;
  is_default: boolean;
  field_mappings: Record<string, FieldMapping>;
}

const sourceSystemOptions: { value: SourceSystem; label: string }[] = [
  { value: 'QUICKBOOKS', label: 'QuickBooks' },
  { value: 'ADP', label: 'ADP' },
  { value: 'PAYCHEX', label: 'Paychex' },
  { value: 'GUSTO', label: 'Gusto' },
  { value: 'WORKDAY', label: 'Workday' },
];

export default function MappingEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // When route is /mappings/new, id is undefined (no :id param in route)
  // When route is /mappings/:id, id has a value
  const isNewMapping = !id;

  const [isLoading, setIsLoading] = useState(!isNewMapping);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [fieldMappings, setFieldMappings] = useState<
    Record<string, FieldMapping>
  >({});

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<MappingFormData>({
    defaultValues: {
      name: '',
      source_system: 'QUICKBOOKS',
      is_default: false,
      field_mappings: {},
    },
  });

  const sourceSystem = watch('source_system');

  const loadMapping = useCallback(
    async (mappingId: string) => {
      try {
        setIsLoading(true);
        const mapping = await mappingApi.getMappingById(mappingId);
        setValue('name', mapping.name);
        setValue('source_system', mapping.source_system as SourceSystem);
        setValue('is_default', mapping.is_default);
        setFieldMappings(mapping.field_mappings);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to load mapping';
        toast.error(errorMessage);
        navigate('/mappings');
      } finally {
        setIsLoading(false);
      }
    },
    [navigate, setValue]
  );

  useEffect(() => {
    if (!isNewMapping && id) {
      loadMapping(id);
    }
  }, [id, isNewMapping, loadMapping]);

  useEffect(() => {
    setHasUnsavedChanges(isDirty || Object.keys(fieldMappings).length > 0);
  }, [isDirty, fieldMappings]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const onSubmit = async (data: MappingFormData) => {
    // Validate that at least one field is mapped
    if (Object.keys(fieldMappings).length === 0) {
      toast.error('Please add at least one field mapping');
      return;
    }

    // Validate that all mappings have both source and target fields
    const invalidMappings = Object.values(fieldMappings).filter(
      mapping => !mapping.source_field || !mapping.target_field
    );

    if (invalidMappings.length > 0) {
      toast.error(
        'All field mappings must have both source and target fields selected'
      );
      return;
    }

    try {
      setIsSaving(true);

      const requestData: MappingRequest = {
        name: data.name,
        source_system: data.source_system,
        field_mappings: fieldMappings,
        is_default: data.is_default,
      };

      if (isNewMapping) {
        await mappingApi.createMapping(requestData);
        toast.success('Mapping created successfully');
      } else if (id) {
        await mappingApi.updateMapping(id, requestData);
        toast.success('Mapping updated successfully');
      }

      setHasUnsavedChanges(false);
      navigate('/mappings');
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to save mapping';
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id || isNewMapping) return;

    if (
      !window.confirm(
        'Are you sure you want to delete this mapping? This action cannot be undone.'
      )
    ) {
      return;
    }

    try {
      setIsDeleting(true);
      await mappingApi.deleteMapping(id);
      toast.success('Mapping deleted successfully');
      navigate('/mappings');
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to delete mapping';
      toast.error(errorMessage);
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    if (
      hasUnsavedChanges &&
      !window.confirm(
        'You have unsaved changes. Are you sure you want to leave?'
      )
    ) {
      return;
    }
    navigate('/mappings');
  };

  const handleFieldMappingsChange = (
    newMappings: Record<string, FieldMapping>
  ) => {
    setFieldMappings(newMappings);
    setHasUnsavedChanges(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading mapping...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={handleCancel}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to Mappings
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            {isNewMapping ? 'Create New Mapping' : 'Edit Mapping'}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            {isNewMapping
              ? 'Configure field mappings for converting payroll data'
              : 'Update your field mapping configuration'}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              Basic Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Mapping Name */}
              <div className="md:col-span-2">
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Mapping Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="name"
                  type="text"
                  {...register('name', {
                    required: 'Mapping name is required',
                    minLength: {
                      value: 3,
                      message: 'Name must be at least 3 characters',
                    },
                  })}
                  className={`w-full rounded-md shadow-sm ${
                    errors.name
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                  }`}
                  placeholder="e.g., QuickBooks Standard Mapping"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.name.message}
                  </p>
                )}
              </div>

              {/* Source System */}
              <div>
                <label
                  htmlFor="source_system"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Source System <span className="text-red-500">*</span>
                </label>
                <select
                  id="source_system"
                  {...register('source_system', {
                    required: 'Source system is required',
                  })}
                  className={`w-full rounded-md shadow-sm ${
                    errors.source_system
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                  }`}
                >
                  {sourceSystemOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.source_system && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.source_system.message}
                  </p>
                )}
              </div>

              {/* Is Default */}
              <div className="flex items-center">
                <div className="flex items-center h-5">
                  <input
                    id="is_default"
                    type="checkbox"
                    {...register('is_default')}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label
                    htmlFor="is_default"
                    className="font-medium text-gray-700"
                  >
                    Set as default mapping
                  </label>
                  <p className="text-gray-500">
                    Use this mapping by default for this source system
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Field Mappings */}
          <div className="bg-white shadow rounded-lg p-6">
            <FieldMapper
              mappings={fieldMappings}
              sourceSystem={sourceSystem}
              onChange={handleFieldMappingsChange}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between bg-white shadow rounded-lg p-6">
            <div>
              {!isNewMapping && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isDeleting || isSaving}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <TrashIcon className="h-4 w-4 mr-2" />
                  {isDeleting ? 'Deleting...' : 'Delete Mapping'}
                </button>
              )}
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleCancel}
                disabled={isSaving || isDeleting}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <XMarkIcon className="h-4 w-4 mr-2" />
                Cancel
              </button>

              <button
                type="submit"
                disabled={isSaving || isDeleting}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <CheckIcon className="h-4 w-4 mr-2" />
                {isSaving
                  ? 'Saving...'
                  : isNewMapping
                    ? 'Create Mapping'
                    : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>

        {/* Unsaved Changes Indicator */}
        {hasUnsavedChanges && (
          <div className="fixed bottom-4 right-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4 shadow-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-yellow-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-yellow-800">
                  You have unsaved changes
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
